// imgLoader.js

/**
 * Loads an image into the given <img> element, tracking success/failure and
 * dispatching a custom 'img.error' event on the document when loading fails.
 *
 * @param {HTMLImageElement} imgElement - The <img> element to load the source into.
 * @param {string} imgSrc              - The image URL to set on the <img> element.
 * @returns {Promise<void>}            - Resolves once the load (or error) event fires.
 */
export function loadImg(imgElement, imgSrc) {
    return new Promise((resolve) => {
        // Initialize global tracking array if not already present
        if (!window.loadedImage) {
            window.loadedImage = [];
        }

        // Remove any previous load-status classes
        imgElement.classList.remove('img-load-error', 'img-load-success');

        // Success handler (once)
        const onLoad = () => {
            imgElement.classList.add('img-load-success');
            window.loadedImage.push(imgSrc);
            resolve();
        };

        // Error handler (once)
        const onError = () => {
            imgElement.classList.add('img-load-error');
            // Dispatch a custom event named 'img.error' with the failed src as detail
            const errorEvent = new CustomEvent('img.error', { detail: imgSrc });
            document.dispatchEvent(errorEvent);
            resolve();
        };

        // Attach event listeners that fire only once
        imgElement.addEventListener('load', onLoad, { once: true });
        imgElement.addEventListener('error', onError, { once: true });

        // If a valid source is provided, update the <img> src only if changed
        if (imgSrc) {
            if (imgElement.getAttribute('src') !== imgSrc) {
                imgElement.setAttribute('src', imgSrc);
            }
        } else {
            // No src given → immediately treat as load error
            imgElement.classList.add('img-load-error');
            const errorEvent = new CustomEvent('img.error', { detail: imgSrc });
            document.dispatchEvent(errorEvent);
            resolve();
        }
    });
}
